import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import {
  flattenCameraListItem,
  flattenCameraRecord,
  getLeftCameraId,
  getMostRecentLogEntry,
  sanitizeNetworkDetails,
} from "./installationConfigUtils";
import {
  getAddressNameList,
  getAzureConfig,
  getAzureCycleLogAndImage,
  getCameraLogDetails,
  getSimCardProvider,
  loadCameraPair,
} from "./installationApi";

async function enrichCameraAddress(api, token, camera) {
  if (
    !camera?.addressId ||
    camera.addressName?.trim() ||
    !camera.corporationId
  ) {
    return camera;
  }

  try {
    const addresses = await getAddressNameList(
      api,
      token,
      camera.corporationId,
    );
    const match = addresses.find((entry) => entry._id === camera.addressId);
    if (match) {
      return { ...camera, addressName: match.name };
    }
  } catch {
    return camera;
  }

  return camera;
}

export function useInstallationCamera(routeCameraId, cameraListItem) {
  const api = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leftCamera, setLeftCamera] = useState(null);
  const [rightCamera, setRightCamera] = useState(null);
  const [hasRightCamera, setHasRightCamera] = useState(false);
  const [configData, setConfigData] = useState({});
  const [cycleEntries, setCycleEntries] = useState([]);
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [networkDetails, setNetworkDetails] = useState(null);
  const [nextWakeupDatetime, setNextWakeupDatetime] = useState(null);
  const [simProvider, setSimProvider] = useState(null);

  const reload = useCallback(
    async ({ silent = false } = {}) => {
      if (!routeCameraId || !token) {
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      setError(null);

      try {
        const pair = await loadCameraPair(api, token, routeCameraId);
        let left =
          flattenCameraRecord(pair.leftCamera) ||
          flattenCameraListItem(cameraListItem);
        let right = pair.rightCamera
          ? flattenCameraRecord(pair.rightCamera)
          : null;

        if (left) {
          left = await enrichCameraAddress(api, token, left);
        }

        const provider = left?.iccid
          ? await getSimCardProvider(left.iccid)
          : null;

        setLeftCamera(left);
        setSimProvider(provider);
        setRightCamera(right);
        setHasRightCamera(pair.hasRightCamera);

        const [config, cycles] = await Promise.all([
          getAzureConfig(api, token, routeCameraId),
          getAzureCycleLogAndImage(api, token, routeCameraId, 1),
        ]);
        setConfigData(config);
        setCycleEntries(cycles);

        const recentLogEntry = getMostRecentLogEntry(cycles);
        if (recentLogEntry?.log && recentLogEntry?.name) {
          try {
            const logDetails = await getCameraLogDetails(
              api,
              token,
              routeCameraId,
              recentLogEntry.name,
            );
            const level = logDetails?.battery;
            setBatteryLevel(
              level !== undefined && level !== null && !Number.isNaN(level)
                ? level
                : null,
            );
            setNetworkDetails(
              sanitizeNetworkDetails({
                networkQuality: logDetails?.networkQuality,
                operator: logDetails?.operator,
              }),
            );
            setNextWakeupDatetime(logDetails?.nextWakeupDatetime || null);
          } catch {
            setBatteryLevel(null);
            setNetworkDetails(null);
            setNextWakeupDatetime(null);
          }
        } else {
          setBatteryLevel(null);
          setNetworkDetails(null);
          setNextWakeupDatetime(null);
        }
      } catch (loadError) {
        const fallback = flattenCameraListItem(cameraListItem);
        if (fallback) {
          setLeftCamera(fallback);
        }
        setError(loadError?.message || "load_failed");
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [api, token, routeCameraId, cameraListItem],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const siteEditId = leftCamera?._id || getLeftCameraId(routeCameraId);

  return {
    api,
    token,
    loading,
    error,
    leftCamera,
    rightCamera,
    hasRightCamera,
    configData,
    setConfigData,
    cycleEntries,
    batteryLevel,
    networkDetails,
    nextWakeupDatetime,
    simProvider,
    siteEditId,
    reload,
  };
}
